import { Component, ElementRef, EventEmitter, HostBinding, Input, OnInit, Output, ViewChild } from '@angular/core';
import { ControlValueAccessor, UntypedFormControl, NG_VALUE_ACCESSOR } from '@angular/forms';

import { LangChangeEvent, TranslateService } from '@ngx-translate/core';

import { Log } from "ng2-logger";

@Component({
    selector: 'app-dropdown',
    templateUrl: './dropdown.component.html',
    styleUrls: ['./dropdown.component.scss'],
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: DropdownComponent,
            multi: true
        }
    ]
})
export class DropdownComponent implements OnInit, ControlValueAccessor {
    _logger = Log.create(DropdownComponent.name);

    @ViewChild('dropdownContainer', {static: true}) dropdownContainer: ElementRef;

    @Input() labelAlign: 'top' | 'left' | 'floating' = 'top';
    @Input() label = '';
    @Input() displayField = 'name';
    @Input() valueField = 'id';
    @Input() emptyText = 'No empty text provided';
    @Input() isDisabled = false;
    @Input() items = [];
    @Input() formControl: UntypedFormControl;
    @Input() showErrors = false;
    @HostBinding('class.m2') @Input() m2 = false;
    @Output() selectItem = new EventEmitter<any>();

    selectActions = new EventEmitter<any>();

    private selectedItem = {};
    private onChangeCallback: (_: any) => {};
    private onToucheCallback: (_: any) => {};

    @HostBinding('class') get labelAlignmentClass(): string {

        if (this.labelAlign === 'left') {
            return 'label-left';
        } else if (this.labelAlign === 'top') {
            return 'label-top';
        } else {
            return 'label-float.float-input'
        }
    }

    get hasError() {
        return this.formControl && this.formControl.errors && this.showErrors;
    }

    constructor(private translateService: TranslateService) {
    }

    ngOnInit() {
        this.translateService.onLangChange.subscribe((event: LangChangeEvent) => {
            this.selectActions.emit({action: 'material_select', params: ['']});
        });
    }

    get value() {
        return this.selectedItem ? this.selectedItem[this.valueField] : this.emptyText;
    }

    trackByFn(index, item) {
        return item[this.valueField]; // or item.id
    }

    writeValue(value) {
        this.updateSelectedItem(value);
    }

    onItemSelect(value) {
        value = this.normalizeValue(value);
        if (this.onChangeCallback) {
            this.onChangeCallback(value);
        }
        this.updateSelectedItem(value);
        this.selectItem.emit(this.selectedItem);
        this._logger.data("Dropdown::onItemSelect", this.selectedItem);
    }

    registerOnChange(fn) {
        this.onChangeCallback = fn;
    }

    registerOnTouched(fn) {
        this.onToucheCallback = fn;
    }

    updateSelectedItem(value) {
        value = this.normalizeValue(value);
        if (this.items) {
            if (value instanceof Object) {
                this.selectedItem = this.items.find(item => item[this.valueField] === value[this.valueField]) || '';
            } else {
                this.selectedItem = this.items.find(item => item[this.valueField] === value) || '';
            }
        }
    }

    preventPropagation(event) {
        event.stopPropagation();
    }

    get leftLabel() {
        return this.label && this.labelAlign === 'left';
    }

    get topLabel() {
        return this.label && this.labelAlign === 'top';
    }

    get floatLabel() {
        return this.label && this.labelAlign === 'floating';
    }

    private normalizeValue(value) {
        if (value === null || value === undefined || value === '') {
            return value;
        }

        const matchingItem = this.items?.find(item => `${item[this.valueField]}` === `${value}`);

        if (matchingItem && typeof matchingItem[this.valueField] === 'number') {
            return +value;
        }

        return value;
    }

}
